import React, { useEffect, useRef } from 'react';
import makeStyles from '@material-ui/core/styles/makeStyles';
import { isLoading, isNormal } from 'resift';
import useAnalytics from 'helpers/useAnalytics';

import _isEmpty from 'lodash/isEmpty';
import _partition from 'lodash/partition';

import Dialog from '@material-ui/core/Dialog';
import Button from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import CsvUploadLoading from 'molecules/csvUpload/CsvUploadLoading';
import CsvUploadIssues from 'molecules/csvUpload/CsvUploadIssues';
import CsvUploadSuccess from './CsvUploadSuccess';

import { MappingValidationResult } from 'types/Mapping';

interface Props {
  open: boolean;
  status: number;
  sourceFileId: string | null;
  sourceId: string;
  validationResults: MappingValidationResult[];
  onNext: () => void;
  onNewUpload: () => void;
}

function CsvAnalysisDialog({
  open,
  status,
  sourceFileId,
  sourceId,
  validationResults,
  onNext,
  onNewUpload,
}: Props) {
  const classes = useStyles();
  const analytics = useAnalytics();
  const hasTrackedForOpen = useRef(false);
  const [errors, warnings] = _partition(validationResults, ['type', 'error']);
  const disabled = !_isEmpty(errors);

  useEffect(() => {
    if (!open) {
      hasTrackedForOpen.current = false;
      return;
    }

    if (
      hasTrackedForOpen.current ||
      isLoading(status) ||
      (isNormal(status) && _isEmpty(validationResults))
    ) {
      return;
    }

    if (errors.length > 0) {
      analytics.track('Data Sync - “Issues Found” Dialog Appear', {
        category: 'AD - Directories',
      });
      hasTrackedForOpen.current = true;
      return;
    }

    if (warnings.length > 0) {
      analytics.track('Data Sync - “Before You Continue” Dialog Appear', {
        category: 'AD - Directories',
      });
      hasTrackedForOpen.current = true;
    }
  }, [analytics, errors.length, open, status, validationResults, warnings.length]);

  if (isLoading(status)) {
    return (
      <Dialog open={open}>
        <div className={classes.root}>
          <CsvUploadLoading />
        </div>
      </Dialog>
    );
  }

  if (isNormal(status) && _isEmpty(validationResults)) {
    return (
      <Dialog open={open}>
        <div className={classes.root}>
          <CsvUploadSuccess sourceFileId={sourceFileId} sourceId={sourceId} />
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog open={open}>
      <div className={classes.root}>
        <CsvUploadIssues errors={errors} warnings={warnings} />
        <div className={classes.buttonContainer}>
          <Button onClick={onNewUpload} color="secondary">
            + Upload New File
          </Button>
          <Tooltip
            arrow
            placement="top"
            title={
              disabled
                ? 'You must fix all errors before continuing. Please upload a new file with the errors addressed.'
                : ''
            }
          >
            <div>
              <Button variant="contained" onClick={onNext} color="secondary" disabled={disabled}>
                Continue
              </Button>
            </div>
          </Tooltip>
        </div>
      </div>
    </Dialog>
  );
}

const useStyles = makeStyles(theme => ({
  root: {
    minWidth: 600,
    height: 740,
    padding: 24,
    display: 'flex',
    flexDirection: 'column',
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    maxHeight: 24,
    marginTop: 'auto',
    position: 'sticky',
    bottom: 0,
  },
}));

export default CsvAnalysisDialog;
